import { shouldEnableMockAdapters } from '@procurify/utils/src/testing/environment'
import axios from 'axios'
import mock from 'axios-mock-adapter'
import chalk from 'chalk'

import { StripeApiClient } from '@webapp/services/api/StripeApiClient'
import { v1, v3 } from '../config/api'

const log = (message, status = null, scope = null) => {
  let finalMessage = ''

  if (status) {
    finalMessage += `${chalk.bgYellow(chalk.black(` ${status} `))} `
  } else {
    finalMessage += '       '
  }

  if (scope) {
    finalMessage += `${chalk.bgWhite.black(` ${scope} `)} `
  }

  finalMessage += message

  return finalMessage
}

export const V3ResponseErrorInterceptor = (error) => {
  if (error.status !== 404) {
    throw error
  }

  const method = error.config.method
  const capitalizedMethod = method.charAt(0).toUpperCase() + method.slice(1)

  const messages = []

  messages.push(
    log(
      chalk.bgRed(chalk.whiteBright(error.message.toString())),
      'WARN',
      'axios'
    )
  )

  messages.push(
    log(
      `${chalk.whiteBright(error.config.method.toUpperCase())} ${chalk.white(
        error.config.url
      )}`,
      null,
      'axios'
    )
  )

  if (error.config.params) {
    messages.push(
      log(
        chalk.cyan(
          `${chalk.whiteBright('Params:')} ${JSON.stringify(
            error.config.params
          )}`
        ),
        null,
        'axios'
      )
    )
  }

  if (error.config.data) {
    messages.push(
      log(
        chalk.white(`${chalk.whiteBright('Data:')} ${error.config.data}`),
        null,
        'axios'
      )
    )
  }

  messages.push(
    log(
      chalk.cyan(
        `${chalk.whiteBright(
          'Suggestion:'
        )} mockAdapter.on${capitalizedMethod}('${error.config.url}')`
      ),
      null,
      'axios'
    )
  )

  const err = error
  err.message = messages.join('\n')

  err.__PROCURIFY_MOCK_AXIOS_ERROR = true
  Error.captureStackTrace(err, V3ResponseErrorInterceptor)

  /**
   * We should look to fail tests if there are endpoints that
   * are not handled. Adding this currently will fail many
   * tests that fails to handle unhandled requests
   *
   * ie. `fail(err)`
   */
  throw err
}

// Only add the interceptor when mock adapters are enabled (test/storybook environments)
if (shouldEnableMockAdapters()) {
  v3.interceptors.response.use((request) => request, V3ResponseErrorInterceptor)
}

const mockAdapters = (() => {
  if (shouldEnableMockAdapters()) {
    return {
      mockAdapter: new mock(v3, { onNoMatch: 'passthrough' }),
      delayedMockAdapter: new mock(v3, {
        onNoMatch: 'passthrough',
        delayResponse: 1500,
      }),
      mockAdapterV1: new mock(v1, { onNoMatch: 'passthrough' }),
      mockAdapterStripeAPI: new mock(StripeApiClient, {
        onNoMatch: 'passthrough',
      }),
    }
  } else {
    console.warn('Mock adapters are disabled in production')

    // We are passing in an unused instance of axios
    // to continue usage of mock adapter without
    // typescript complaining this might be null
    const mockInstance = axios.create()
    return {
      mockAdapter: new mock(mockInstance),
      delayedMockAdapter: new mock(mockInstance),
      mockAdapterV1: new mock(mockInstance),
      mockAdapterStripeAPI: new mock(mockInstance),
    }
  }
})()

export const {
  mockAdapter,
  delayedMockAdapter,
  mockAdapterV1,
  mockAdapterStripeAPI,
} = mockAdapters

export const expectNoOutstandingRequests = () => {
  Object.keys((mockAdapter as any).handlers).map((method) => {
    if ((mockAdapter as any).handlers[method].length !== 0) {
      const handler = (mockAdapter as any).handlers[method][0]

      const error = Error(
        `Expecting ${chalk.bgRed(
          ` ${method.toUpperCase()} `
        )} ${chalk.cyanBright(handler[0])} to be called, but it never did` +
          `\n\n` +
          `Did you use reply instead of replyOnce?`
      )

      Error.captureStackTrace(error, expectNoOutstandingRequests)

      throw error
    }
  })
}

export const expectNoOutstandingV1Requests = () => {
  Object.keys((mockAdapterV1 as any).handlers).map((method) => {
    if ((mockAdapterV1 as any).handlers[method].length !== 0) {
      const handler = (mockAdapterV1 as any).handlers[method][0]

      const error = Error(
        `Expecting ${chalk.bgRed(
          ` ${method.toUpperCase()} `
        )} ${chalk.cyanBright(handler[0])} to be called, but it never did` +
          `\n\n` +
          `Did you use reply instead of replyOnce?`
      )

      Error.captureStackTrace(error, expectNoOutstandingRequests)

      throw error
    }
  })
}
